/**
 * Les soirées passées, gardées sur ce téléphone seulement.
 *
 * Le serveur oublie tout à la clôture : c'est la promesse. Ce que l'écran
 * « Moi » montre — le sujet, le lieu, le nombre de présents, la réponse
 * commune, les points gagnés — vit donc ici, dans le navigateur, et nulle
 * part ailleurs — chat compris, pour le relire (« Relire » sur une soirée
 * passée). Avec un compte, les points et un résumé de chaque soirée sont
 * aussi tenus par le serveur (profil.ts), qui fait foi pour les points.
 */

export interface SoireePassee {
  id: string;
  titre: string;
  /** Heure du rendez-vous. */
  rdvAt: number;
  rejointA: number;
  lieu: string;
  personnes: number;
  reunis: boolean;
  /** Pseudos des humains retrouvés sur place (sans soi). */
  avec: string[];
  pointCommun: string | null;
  /** Points gagnés ce soir-là (0 au-delà de deux rencontres dans la journée). */
  gain?: number;
  /** Le chat de la tribu, pour le relire (sur ce téléphone seulement). */
  chat?: Array<{ id: string; pid: string; pseudo: string; text: string; at: number }>;
  /** Son identifiant ce soir-là, pour reconnaître ses propres messages. */
  moi?: string | null;
  /**
   * Les autres membres de la tribu (identifiant du soir et pseudo), pour
   * pouvoir en signaler un après coup. Le serveur garde la même liste sept
   * jours, pas davantage.
   */
  membres?: Array<{ pid: string; pseudo: string }>;
}

/** Sept jours : au-delà, le serveur a oublié qui était dans la tribu. */
export const SIGNALABLE_MS = 7 * 86_400_000;

/** Les membres qu'on peut viser : ceux gardés, ou à défaut ceux qui ont écrit. */
export function membresDe(s: SoireePassee): Array<{ pid: string; pseudo: string }> {
  if (s.membres && s.membres.length) return s.membres;
  const vus = new Map<string, string>();
  for (const l of s.chat ?? []) if (l.pid !== s.moi && !vus.has(l.pid)) vus.set(l.pid, l.pseudo);
  return [...vus].map(([pid, pseudo]) => ({ pid, pseudo }));
}

/** Une soirée où l'on peut encore signaler quelqu'un : récente, avec son identifiant et d'autres membres. */
export function signalable(s: SoireePassee, maintenant: number): boolean {
  return Boolean(s.moi) && maintenant - s.rdvAt < SIGNALABLE_MS && membresDe(s).length > 0;
}

/** Une rencontre vaut dix points ; deux rencontres par jour au plus sont récompensées. */
/**
 * Les filtres de « Tes soirées ». Hoa sert à se retrouver en vrai : la seule
 * distinction qui compte est de savoir si la rencontre a eu lieu. Le gain est
 * le même pour chaque rencontre et le nombre de présents ne dit rien de la
 * soirée : ni l'un ni l'autre ne classe quoi que ce soit.
 */
export type FiltreSoirees = "toutes" | "reunis" | "sans";
export const FILTRES_SOIREES: Array<[FiltreSoirees, string]> = [
  ["toutes", "Toutes"],
  ["reunis", "Réunis"],
  ["sans", "Sans rencontre"],
];

/** Les soirées du filtre, la plus récente d'abord. */
export function filtrerSoirees(soirees: SoireePassee[], filtre: FiltreSoirees): SoireePassee[] {
  return soirees.filter((s) => filtre === "toutes" || (filtre === "reunis") === s.reunis).sort((a, b) => b.rdvAt - a.rdvAt);
}

/** Un filtre ne sert que s'il sépare quelque chose : il faut des soirées des deux sortes. */
export function filtresUtiles(soirees: SoireePassee[]): boolean {
  return soirees.some((s) => s.reunis) && soirees.some((s) => !s.reunis);
}

export const GAIN_RENCONTRE = 10;
export const RENCONTRES_RECOMPENSEES_PAR_JOUR = 2;

function memeJour(a: number, b: number): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

/** Ce que rapporterait une rencontre à cette date, vu les soirées déjà gardées. */
export function gainPour(soirees: SoireePassee[], rdvAt: number, sauf?: string): number {
  const deja = soirees.filter((s) => s.id !== sauf && (s.gain ?? 0) > 0 && memeJour(s.rdvAt, rdvAt)).length;
  return deja < RENCONTRES_RECOMPENSEES_PAR_JOUR ? GAIN_RENCONTRE : 0;
}

export function totalPoints(soirees: SoireePassee[]): number {
  return soirees.reduce((n, s) => n + (s.gain ?? 0), 0);
}

const CLE = "hoa.soirees";

export function lireSoirees(): SoireePassee[] {
  try {
    const brut = JSON.parse(localStorage.getItem(CLE) ?? "[]") as SoireePassee[];
    return Array.isArray(brut) ? brut.filter((s) => s && typeof s.id === "string") : [];
  } catch {
    return [];
  }
}

/**
 * Garde la soirée. Les points se calculent ici, une seule fois : une même
 * soirée gardée deux fois ne rapporte pas deux fois.
 */
export function garderSoiree(s: SoireePassee): void {
  try {
    const avant = lireSoirees();
    const connue = avant.find((x) => x.id === s.id);
    s.gain = connue?.gain ?? (s.reunis ? gainPour(avant, s.rdvAt, s.id) : 0);
    const liste = avant.filter((x) => x.id !== s.id);
    liste.unshift(s);
    localStorage.setItem(CLE, JSON.stringify(liste.slice(0, 50)));
  } catch {
    /* navigation privée : la soirée n'est pas gardée */
  }
}
